import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';
import { PageTitle } from '../../../_metronic/layout/core';
import DateRangeSelector from '../../components/DateRangeSelector';
import { authUserSelector } from '../../modules/auth';
import {
  ExternalOrderList,
  ExternalOrderModel,
  ExternalOrderStateEnum,
  BulkActionsEnum,
  BulkActionConfig,
  listExternalOrders,
  performBulkAction,
} from '../../modules/external-orders';

const DEBOUNCE_TIMEOUT = 800;

const BASE_BULK_ACTIONS: BulkActionConfig[] = [
  {
    action: BulkActionsEnum.PREPARE_ORDERS,
    i18nKey: 'prepareOrders',
    icon: 'fa-magic',
  },
];

const CANCEL_ACTION: BulkActionConfig = {
  action: BulkActionsEnum.CANCEL_ORDERS,
  i18nKey: 'cancelOrders',
  icon: 'fa-ban',
};

const BARAKALDO_EMAIL_DOMAIN = '@barakaldotiendaveterinaria.es';

const ExternalOrdersPage: FC = () => {
  const intl = useIntl();
  const authUser = useSelector(authUserSelector, shallowEqual);
  const isBarakaldo = authUser?.email?.endsWith(BARAKALDO_EMAIL_DOMAIN);
  const bulkActions = isBarakaldo
    ? [...BASE_BULK_ACTIONS, CANCEL_ACTION]
    : BASE_BULK_ACTIONS;
  const [orders, setOrders] = useState<Array<ExternalOrderModel>>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [dateRange, setDateRange] = useState<{
    since: Date | null;
    until: Date | null;
  }>({ since: null, until: null });
  const [status, setStatus] = useState<ExternalOrderStateEnum | null>(null);
  const [number, setNumber] = useState<string | null>(null);

  const statusOptions = [
    {
      value: null,
      label: intl.formatMessage({ id: 'externalOrders.allStatuses' }),
    },
    {
      value: ExternalOrderStateEnum.RECEIVED,
      label: intl.formatMessage({ id: 'externalOrders.statusReceived' }),
    },
    {
      value: ExternalOrderStateEnum.PROCESSING,
      label: intl.formatMessage({ id: 'externalOrders.statusProcessing' }),
    },
    {
      value: ExternalOrderStateEnum.PROCESSED,
      label: intl.formatMessage({ id: 'externalOrders.statusProcessed' }),
    },
    {
      value: ExternalOrderStateEnum.ERROR,
      label: intl.formatMessage({ id: 'externalOrders.statusError' }),
    },
    {
      value: ExternalOrderStateEnum.CANCELLED,
      label: intl.formatMessage({ id: 'externalOrders.statusCancelled' }),
    },
  ];

  const formatDate = (date: Date | null) =>
    date
      ? date.toLocaleDateString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      : undefined;

  async function fetchOrders() {
    try {
      setLoading(true);
      const { data } = await listExternalOrders({
        page,
        date__gte: formatDate(dateRange.since),
        date__lte: formatDate(dateRange.until),
        status: status || undefined,
        number: number || undefined,
      });
      setOrders(data?.results);
      setCount(data?.count);
    } catch (error) {
      console.warn(error);
    } finally {
      setLoading(false);
    }
  }

  const fetchDependencies = [dateRange, status, number];

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchOrders();
    }, DEBOUNCE_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
  }, [...fetchDependencies, page]);

  useEffect(() => {
    setPage(1);
    setCount(0);
  }, fetchDependencies);

  async function handleBulkAction(
    action: BulkActionsEnum,
    orderIds: Array<number>
  ) {
    await performBulkAction({
      orders: orderIds,
      action,
      date__gte: formatDate(dateRange.since),
      date__lte: formatDate(dateRange.until),
      status: status || undefined,
      number: number || undefined,
    });
    fetchOrders();
  }

  return (
    <>
      <PageTitle>
        {intl.formatMessage({ id: 'externalOrders.title' })}
      </PageTitle>
      <ExternalOrderList
        orders={orders}
        page={page}
        count={count}
        onPageChange={(p) => {
          setPage(p);
        }}
        loading={loading}
        displayBulkActions={true}
        bulkActions={bulkActions}
        onBulkAction={handleBulkAction}
      >
        <div className="col-auto d-flex flex-column">
          <label className="form-label fw-bold">
            {intl.formatMessage({ id: 'externalOrders.filterByNumber' })}
          </label>
          <input
            type="text"
            className="form-control w-200px"
            placeholder={intl.formatMessage({
              id: 'externalOrders.numberPlaceholder',
            })}
            value={number || ''}
            onChange={(e) => setNumber(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <DateRangeSelector
            label={intl.formatMessage({ id: 'externalOrders.filterByDate' })}
            onSelected={(since, until) => {
              setPage(1);
              setDateRange({ since, until });
            }}
            initialSince={dateRange.since}
            initialUntil={dateRange.until}
          />
        </div>
        <div className="col-auto d-flex flex-column">
          <label className="form-label fw-bold">
            {intl.formatMessage({ id: 'externalOrders.filterByStatus' })}
          </label>
          <select
            className="form-select w-200px"
            value={status ?? ''}
            onChange={(e) => {
              setPage(1);
              setStatus((e.target.value as ExternalOrderStateEnum) || null);
            }}
          >
            {statusOptions.map((opt) => (
              <option key={opt.value ?? ''} value={opt.value ?? ''}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </ExternalOrderList>
    </>
  );
};

export { ExternalOrdersPage };
